import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';
import { FloorplansFallback } from './FloorplansFallback';

// ============================================================================
// Component
// ============================================================================

/**
 * Thin theme wrapper around the `apartment-sync` block. Lets the section
 * appear as a normal registered component in `pages.json` while delegating
 * rendering to the block. The wrapped block is swappable via the editor
 * (constrained to the Integrations category).
 *
 * Dietz override: the ApartmentSync (Repli360 / RRAC) widget renders nothing
 * until a property supplies its `javascript_id` / `config_id` content tokens.
 * When those aren't set, fall back to the default `floorplans` block so the
 * Floor Plans page still shows the property's floorplan listings.
 */
export function ApartmentSyncSection() {
  const blockSlot = useBlockSlot('apartmentsyncsection.block', {
    defaultValue: 'apartment-sync',
  });

  // Mirror the apartment-sync block's own token resolution so we can tell
  // whether it has the content it needs to render. `useBlockToken` inside the
  // block resolves `${sectionId}.<name>` with these same default fields.
  const syncSectionId = blockSlot?.sectionId ?? 'apartmentsyncsection.block';
  const javascriptId = useContentToken(`${syncSectionId}.javascript_id`, {
    defaultField: 'custom_apartmentsync_javascript_id',
  });

  // Only fall back when the unconfigured block is the default apartment-sync
  // widget — if the editor swapped in a different Integrations block, respect
  // that choice and render it as-is. The widget renders nothing without a
  // `javascript_id`, so that's the single token that gates the fallback.
  const isApartmentSync = !blockSlot || blockSlot.blockId === 'apartment-sync';
  const useFallback = isApartmentSync && !javascriptId;

  return (
    <section className="apartment-sync-section bg-background">
      <EditableToken path="apartmentsyncsection.block" as="div" className="apartment-sync-section__inner">
        {useFallback ? (
          <FloorplansFallback />
        ) : blockSlot ? (
          <SectionContextProvider sectionId={blockSlot.sectionId}>
            <blockSlot.BlockComponent sectionId={blockSlot.sectionId} />
          </SectionContextProvider>
        ) : (
          <div className="apartment-sync-empty">No block selected</div>
        )}
      </EditableToken>

      <style>{`
        .apartment-sync-section {
          width: 100%;
          min-height: 400px;
        }
        .apartment-sync-section__inner {
          min-height: 400px;
        }
        .apartment-sync-empty {
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 2rem 1rem;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'ApartmentSyncSection',
  displayName: 'Apartment Sync Section',
  tokens: {
    'apartmentsyncsection.block': {
      label: 'Block',
      type: 'block',
      defaultValue: 'apartment-sync',
      acceptedBlockCategories: ['Integrations'],
    },
  },
};

(ApartmentSyncSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ApartmentSyncSection;
